import { inject, Injectable } from '@angular/core';
import { WINDOW_TOKEN } from '../../tokens/app-tokens';

type CampaignParams = Record<string, string>;

@Injectable({ providedIn: 'root' })
export class CampaignParamsService {
  private readonly window = inject(WINDOW_TOKEN);
  private readonly sessionStorageKey = 'matomo-campaign-params';
  private readonly supportedCampaignParamKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'mtm_campaign',
    'mtm_source',
    'mtm_medium',
    'mtm_keyword',
    'mtm_content',
    'pk_campaign',
    'pk_kwd',
    'gclid',
    'fbclid',
  ] as const;

  captureFromUrl(rawUrl: string): void {
    const currentCampaignParams = this.extractCampaignParams(rawUrl);
    if (Object.keys(currentCampaignParams).length === 0) {
      return;
    }

    const mergedCampaignParams = { ...this.readStoredCampaignParams(), ...currentCampaignParams };
    this.storeCampaignParams(mergedCampaignParams);
  }

  getTrackingUrl(rawUrl: string): string {
    const currentCampaignParams = this.extractCampaignParams(rawUrl);
    const mergedCampaignParams = { ...this.readStoredCampaignParams(), ...currentCampaignParams };

    if (Object.keys(mergedCampaignParams).length > 0) {
      this.storeCampaignParams(mergedCampaignParams);
    }

    return this.buildTrackingUrl(rawUrl, mergedCampaignParams);
  }

  private buildTrackingUrl(rawUrl: string, campaignParams: CampaignParams): string {
    if (Object.keys(campaignParams).length === 0) {
      return rawUrl;
    }

    try {
      const url = new URL(rawUrl);
      for (const [key, value] of Object.entries(campaignParams)) {
        if (!url.searchParams.has(key)) {
          url.searchParams.set(key, value);
        }
      }
      return url.toString();
    } catch {
      return rawUrl;
    }
  }

  private extractCampaignParams(rawUrl: string): CampaignParams {
    try {
      const url = new URL(rawUrl);
      const params: CampaignParams = {};

      for (const key of this.supportedCampaignParamKeys) {
        const value = url.searchParams.get(key);
        if (value) {
          params[key] = value;
        }
      }

      return params;
    } catch {
      return {};
    }
  }

  private readStoredCampaignParams(): CampaignParams {
    const sessionStorage = this.getSessionStorage();
    if (!sessionStorage) {
      return {};
    }

    try {
      const rawValue = sessionStorage.getItem(this.sessionStorageKey);
      if (!rawValue) {
        return {};
      }

      return this.normalizeCampaignParams(JSON.parse(rawValue));
    } catch {
      return {};
    }
  }

  private normalizeCampaignParams(value: unknown): CampaignParams {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {};
    }

    const valueAsRecord = value as Record<string, unknown>;
    const params: CampaignParams = {};

    for (const key of this.supportedCampaignParamKeys) {
      const paramValue = valueAsRecord[key];
      if (typeof paramValue === 'string' && paramValue.trim()) {
        params[key] = paramValue;
      }
    }

    return params;
  }

  private storeCampaignParams(campaignParams: CampaignParams): void {
    const sessionStorage = this.getSessionStorage();
    if (!sessionStorage) {
      return;
    }

    try {
      sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(campaignParams));
    } catch {
      // Ignore storage errors to avoid breaking tracking.
    }
  }

  private getSessionStorage(): Storage | null {
    try {
      return this.window.sessionStorage;
    } catch {
      return null;
    }
  }
}
